import axios from 'axios';
import { getTokens } from './authApi';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api') + '/admin';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const tokens = getTokens();
  return tokens?.accessToken ? {
    Authorization: `Bearer ${tokens.accessToken}`
  } : {};
};

export interface TechnicianSchedule {
  id?: number;
  user_id: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ScheduleException {
  id?: number;
  user_id: number;
  exception_type: 'time_off' | 'break' | 'special_hours' | 'holiday';
  start_datetime: string;
  end_datetime: string;
  reason?: string;
  is_recurring: boolean;
  recurrence_pattern?: {
    frequency: 'weekly' | 'monthly';
    interval: number;
  };
  created_at?: string;
  updated_at?: string;
}

export interface BookingSettings {
  id?: number;
  allow_customer_booking: boolean;
  max_daily_appointments: number;
  buffer_time_minutes: number;
  booking_advance_days_min: number;
  booking_advance_days_max: number;
  allow_diagnosis_booking: boolean;
  allow_repair_booking: boolean;
  allow_inspection_booking: boolean;
  default_diagnosis_duration_minutes: number;
  default_repair_duration_minutes: number;
  default_inspection_duration_minutes: number;
  auto_assign_technician: boolean;
  prefer_nearest_technician: boolean;
  balance_workload: boolean;
  send_confirmation_email: boolean;
  send_confirmation_sms: boolean;
  send_reminder_email: boolean;
  send_reminder_sms: boolean;
  reminder_hours_before: number;
  custom_booking_hours: boolean;
  booking_start_time?: string;
  booking_end_time?: string;
  created_at?: string;
  updated_at?: string;
}

export const scheduleApi = {
  // Technician Schedules
  getAllTechniciansWithSchedules: async () => {
    const response = await axios.get(`${API_BASE_URL}/schedules/technicians`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  getTechnicianSchedule: async (userId: number): Promise<TechnicianSchedule[]> => {
    const response = await axios.get(`${API_BASE_URL}/schedules/technicians/${userId}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  updateTechnicianSchedule: async (userId: number, schedules: Omit<TechnicianSchedule, 'id' | 'user_id' | 'created_at' | 'updated_at'>[]): Promise<TechnicianSchedule[]> => {
    const response = await axios.put(`${API_BASE_URL}/schedules/technicians/${userId}`, schedules, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  deleteTechnicianSchedule: async (userId: number): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/schedules/technicians/${userId}`, {
      headers: getAuthHeaders()
    });
  },

  // Schedule Exceptions
  getExceptions: async (params: { user_id?: number; start_date?: string; end_date?: string }): Promise<ScheduleException[]> => {
    const response = await axios.get(`${API_BASE_URL}/schedules/exceptions`, {
      params,
      headers: getAuthHeaders()
    });
    return response.data;
  },

  createException: async (exception: Omit<ScheduleException, 'id' | 'created_at' | 'updated_at'>): Promise<ScheduleException> => {
    const response = await axios.post(`${API_BASE_URL}/schedules/exceptions`, exception, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  updateException: async (id: number, exception: Partial<ScheduleException>): Promise<ScheduleException> => {
    const response = await axios.put(`${API_BASE_URL}/schedules/exceptions/${id}`, exception, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  deleteException: async (id: number): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/schedules/exceptions/${id}`, {
      headers: getAuthHeaders()
    });
  },

  // Booking Settings
  getBookingSettings: async (): Promise<BookingSettings> => {
    const response = await axios.get(`${API_BASE_URL}/schedules/booking-settings`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  updateBookingSettings: async (settings: Partial<BookingSettings>): Promise<BookingSettings> => {
    const response = await axios.put(`${API_BASE_URL}/schedules/booking-settings`, settings, {
      headers: getAuthHeaders()
    });
    return response.data;
  }
};
